import {
  displayLengthToWorld,
  worldToDisplayLength,
} from '../../../utils/conversions';
import { editorStore } from '../../../store/editorStore';
import { canonicalizeFillPattern } from '../../../utils/fillPatterns';
import {
  CircleSymbolType,
  RulerLineType,
  SingleLineType,
  SvgType,
  Symbol3DMetadata,
  SymbolType,
  TriangleSymbolType,
  Wall3DMetadata,
  WallType,
  isCircleStairsType,
  isCircleSymbolType,
  isDoubleDoorType,
  isDoorType,
  isSlidingDoorType,
  isRectStairsType,
  isTurnStairsType,
  isRulerLineType,
  isSquareSymbolType,
  isSvgType,
  isTriangleSymbolType,
  isVoidType,
  isWindowType,
} from '../../../types/floorplannerTypes';

export const mixedValuePlaceholder = 'Mixed';

export const panelFieldClassName = 'flex w-full min-w-0 flex-col items-stretch';
/** Full-width field for two-column grids (avoids cramped wrapping on long locales). */
export const panelGridFieldClassName =
  'flex w-full min-w-0 flex-col items-stretch';
/**
 * Field labels: sentence case, quiet hierarchy above inputs (no all-caps).
 * No fixed min-height — parent grids use items-end so wrapped labels (e.g.
 * Swedish "Avstånd från golv") still keep inputs level without extra gap.
 */
export const panelFieldLabelClassName =
  'mb-0.5 block w-full min-w-0 text-[11px] font-medium leading-4 text-gray-600';
/** Inline row labels beside color / style / pattern controls. */
export const panelRowLabelClassName =
  'shrink-0 text-[11px] font-medium leading-4 text-gray-600';
/** Label-left / control-right row used by Line & Fill appearance menus. */
export const panelAppearanceRowClassName =
  'flex min-h-[28px] flex-row items-center justify-between gap-2 py-1';
/** Group headers like Sizing / Table / Seating within an object section. */
export const panelSubsectionTitleClassName =
  'px-3 pt-3 pb-1 text-[12px] font-medium leading-4 text-gray-800';
/** Outer row that wraps UnitHeader for object names. */
export const panelObjectTitleRowClassName =
  'flex min-h-[36px] items-center bg-boniYellowPrimary px-3 py-2';
/** Compact value inputs — denser than Materials-adjacent controls but still tappable. */
export const panelFieldInputClassName =
  'h-8 w-full rounded-sm border border-gray-300 bg-white px-1.5 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500 dark:disabled:border-gray-600 dark:disabled:bg-gray-800 dark:disabled:text-gray-500';
/**
 * Compact select / text control used in Line & Fill rows.
 * Keep height tall enough that native select labels are fully visible.
 */
export const panelRowControlClassName =
  'box-border h-8 w-[88px] shrink-0 rounded-sm border border-gray-300 bg-white px-1.5 py-0 text-[12px] leading-normal text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400';
/**
 * Compact color swatch (square) for Line/Fill rows — avoids the oversized
 * native color input chrome that looks like a box-in-a-box.
 */
export const panelColorSwatchClassName =
  'h-8 w-8 shrink-0 cursor-pointer rounded-sm border border-gray-300 bg-white p-0 disabled:cursor-not-allowed disabled:opacity-50 [&::-moz-color-swatch]:rounded-[2px] [&::-moz-color-swatch]:border-0 [&::-webkit-color-swatch]:rounded-[2px] [&::-webkit-color-swatch]:border-0 [&::-webkit-color-swatch-wrapper]:p-0';

export const areNumbersEqual = (left: number, right: number, tolerance = 0.01) => {
  return Math.abs(left - right) <= tolerance;
};

export const getCommonValue = <T, TValue>(
  items: T[],
  getValue: (item: T) => TValue,
  compare?: (left: TValue, right: TValue) => boolean,
): TValue | undefined => {
  if (items.length === 0) {
    return undefined;
  }

  const firstValue = getValue(items[0]);
  const isEqual = compare ?? ((left: TValue, right: TValue) => left === right);

  return items.every((item) => isEqual(getValue(item), firstValue)) ? firstValue : undefined;
};

export const hexColorToNumber = (hexColor: string) => {
  return parseInt(hexColor.startsWith('#') ? hexColor.slice(1) : hexColor, 16);
};

export const numberToPanelHexColor = (color: number | string) => {
  if (typeof color === 'string') {
    const normalized = color.trim().toLowerCase();
    if (/^#[0-9a-f]{6}$/.test(normalized)) return normalized;
    if (/^[0-9a-f]{6}$/.test(normalized)) return `#${normalized}`;
    if (normalized === 'black') return '#000000';
    if (normalized === 'white') return '#ffffff';
  }

  const numericColor =
    typeof color === 'number' && Number.isFinite(color) ? color : 0;
  return `#${numericColor.toString(16).padStart(6, '0').slice(-6)}`;
};

export const toDisplayLength = (worldValue: number) =>
  worldToDisplayLength(worldValue, editorStore.displayLengthUnit);

export const toWorldFromDisplay = (displayValue: number) =>
  displayLengthToWorld(displayValue, editorStore.displayLengthUnit);

/** @deprecated Prefer toDisplayLength */
export const toMillimeters = toDisplayLength;
/** @deprecated Prefer toWorldFromDisplay */
export const toWorld = toWorldFromDisplay;

export const radiansToRoundedDegrees = (radians = 0) => Math.round((radians * 180) / Math.PI);
export const degreesToRadians = (degrees: number) => (degrees * Math.PI) / 180;

export const clampDoorAngleDegrees = (degrees: number) => Math.max(0, Math.min(90, Math.round(degrees || 0)));

export const getDisplayedObjects = <T,>(objects: T[]) => (objects.length > 1 ? objects.slice(0, 1) : objects);

/** Common length across objects in the active display unit (mm or in). */
export const getCommonDisplayLengthValue = <T,>(
  objects: T[],
  getValue: (object: T) => number | undefined,
) =>
  getCommonValue(
    objects,
    (object) => toDisplayLength(getValue(object) || 0),
    areNumbersEqual,
  );

/** @deprecated Prefer getCommonDisplayLengthValue */
export const getCommonMillimeterValue = getCommonDisplayLengthValue;

export const getCommonDegreesValue = <T extends { rotation?: number }>(objects: T[]) =>
  getCommonValue(objects, (object) => radiansToRoundedDegrees(object.rotation || 0), areNumbersEqual);

export type LineWeightDefaults = {
  wallLineWeight: number;
  singleLineWeight: number;
  rulerLineWeight: number;
  windowLineWeight: number;
  doorLineWeight: number;
  symbolLineWeight: number;
  lineWeight: number;
};

export const getObjectLineWeight = (
  object: WallType | SymbolType | SingleLineType | RulerLineType,
  defaults: LineWeightDefaults,
) => {
  return (
    object.lineWeight ??
    (object.type === 'wall'
      ? defaults.wallLineWeight
      : object.type === 'singleLine'
        ? defaults.singleLineWeight
        : object.type === 'rulerLine'
          ? defaults.rulerLineWeight
          : object.type === 'window'
            ? defaults.windowLineWeight
            : object.type === 'door' || object.type === 'slidingDoor' || object.type === 'doubleDoor'
              ? defaults.doorLineWeight
              : object.type === 'circleStairs' ||
                  object.type === 'rectStairs' ||
                  object.type === 'turnStairs' ||
                  object.type === 'square' ||
                  object.type === 'circle' ||
                  object.type === 'triangle' ||
                  object.type === 'svg'
                ? defaults.symbolLineWeight
                : defaults.lineWeight)
  );
};

export const getObjectLineColor = (object: WallType | SymbolType | SingleLineType | RulerLineType, fallbackLineColor: number) => {
  const colorValue = object.lineColor !== undefined && object.lineColor !== null ? object.lineColor : fallbackLineColor;

  return numberToPanelHexColor(colorValue);
};

export const formatBackdropMillimeters = (value?: number) => {
  if (!Number.isFinite(value) || !value || value <= 0) return 'Not set';
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} mm`;
};

export const formatBackdropPercent = (value?: number) => {
  if (!Number.isFinite(value) || !value || value <= 0) return 'Not set';
  return `${(value * 100).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
};

export const formatLightingTimeOfDay = (value: number) => {
  const clamped = Math.max(0, Math.min(1, value));
  const minutes = Math.round(360 + clamped * 900);
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

/** Returns an i18n key for the symbol type label; translate at display with t(). */
export const getSymbolLabelKey = (symbol: SymbolType | undefined): string => {
  if (isDoorType(symbol)) return 'propertyPanel.door';
  if (isSlidingDoorType(symbol)) {
    return canonicalizeFillPattern(symbol.fillPattern) === 'glass' ||
      symbol.libraryKey === 'MANUAL/GlassSlidingDoor'
      ? 'propertyPanel.glassSlidingDoor'
      : 'propertyPanel.slidingDoor';
  }
  if (isDoubleDoorType(symbol)) return 'propertyPanel.doubleDoorLabel';
  if (isCircleStairsType(symbol)) return 'propertyPanel.circularStairsLabel';
  if (isRectStairsType(symbol)) return 'propertyPanel.rectangularStairsLabel';
  if (isTurnStairsType(symbol)) return 'propertyPanel.turnStairsLabel';
  if (isSquareSymbolType(symbol)) return 'propertyPanel.square';
  if (isCircleSymbolType(symbol)) return 'propertyPanel.circle';
  if (isTriangleSymbolType(symbol)) return 'propertyPanel.triangle';
  if (isSvgType(symbol)) return 'propertyPanel.svgSymbol';
  if (isWindowType(symbol)) return 'propertyPanel.window';
  if (isVoidType(symbol)) return 'symbols.void';
  if (isRulerLineType(symbol)) return 'propertyPanel.ruler';
  if (symbol?.type === 'area') return 'propertyPanel.measureArea';
  if (symbol?.type === 'areaFill') return 'propertyPanel.roomAreaFill';
  return 'propertyPanel.symbolGeneric';
};

/** @deprecated Use getSymbolLabelKey and translate at display. */
export const getSymbolLabel = getSymbolLabelKey;

/**
 * Drop Ready/catalog wall material fields (same idea as furniture slot delete).
 * Prefer omitting keys over `materialId: undefined` / `surfaceRef: undefined`.
 */
export function omitWallSurfaceMaterial(
  threeD: Wall3DMetadata | undefined,
): Wall3DMetadata | undefined {
  if (!threeD) return undefined;
  const { materialId: _materialId, surfaceRef: _surfaceRef, ...rest } = threeD;
  return Object.keys(rest).length > 0 ? rest : undefined;
}

/**
 * Drop direct surface material fields on symbols (area fills, shapes, etc.).
 * Matches furniture clear: delete keys instead of leaving `undefined` placeholders.
 */
export function omitSymbolDirectSurfaceMaterial(
  threeD: Symbol3DMetadata | undefined,
  options: {
    clearAssignments?: boolean;
    clearFillColorOverride?: boolean;
  } = {},
): Symbol3DMetadata | undefined {
  if (!threeD) return undefined;
  const {
    surfaceRef: _surfaceRef,
    materialAssignments: _materialAssignments,
    fillColorOverride: _fillColorOverride,
    ...rest
  } = threeD;

  const next: Symbol3DMetadata = { ...rest };
  if (!options.clearAssignments && threeD.materialAssignments) {
    next.materialAssignments = threeD.materialAssignments;
  }
  if (!options.clearFillColorOverride && threeD.fillColorOverride !== undefined) {
    next.fillColorOverride = threeD.fillColorOverride;
  }
  return Object.keys(next).length > 0 ? next : undefined;
}

export type AspectRatioShape = CircleSymbolType | TriangleSymbolType | SvgType | (SymbolType & {
  squareWidth?: number;
  squareHeight?: number;
  ratio?: number;
  lockAspectRatio?: boolean;
});
